import { useEffect, useState } from "react";
import { rpc } from "../../../lib/rpc";
import { hasQuantity } from "../../../lib/quantityInput";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { QuantityStepper } from "../../../components/mobile/QuantityStepper";
import { Label } from "../../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";

const STATUS_LABELS = {
    open: "Offen",
    in_progress: "Reserviert",
};

export default function PicklisteFlow() {
    const flow = useFlowOperationId();
    const [picks, setPicks] = useState([]);
    const [pickId, setPickId] = useState("");
    const [items, setItems] = useState([]);
    const [articles, setArticles] = useState({});
    const [positions, setPositions] = useState({});
    const [compartments, setCompartments] = useState({});
    const [picked, setPicked] = useState({});
    const [reserveBusy, setReserveBusy] = useState(false);
    const [completeBusy, setCompleteBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const loadLists = async () => {
        const response = await rpc.get("/picklists");
        if (!response.ok) {
            setFeedback({ type: "error", text: response.error });
            return;
        }
        setPicks(
            response.data.filter((pick) =>
                ["open", "in_progress"].includes(pick.status)
            )
        );
    };

    const loadItems = async (listId) => {
        const response = await rpc.get(`/picklists/${listId}/items`);
        if (!response.ok) {
            setFeedback({ type: "error", text: response.error });
            return;
        }
        setItems(response.data);
        setPicked(
            Object.fromEntries(
                response.data.map((item) => [
                    item.id,
                    item.picked_quantity || item.requested_quantity,
                ])
            )
        );
    };

    useEffect(() => {
        Promise.all([
            rpc.get("/master/articles"),
            rpc.get("/master/stock-positions"),
            rpc.get("/master/compartments"),
        ]).then(([response, positionResponse, compartmentResponse]) => {
            if (response.ok) {
                setArticles(
                    Object.fromEntries(response.data.map((article) => [article.id, article]))
                );
            } else {
                setFeedback({ type: "error", text: response.error });
            }
            if (positionResponse.ok) {
                setPositions(Object.fromEntries(positionResponse.data.map((position) => [position.id, position])));
            }
            if (compartmentResponse.ok) {
                setCompartments(Object.fromEntries(compartmentResponse.data.map((compartment) => [compartment.id, compartment])));
            }
        });
        loadLists();
    }, []);

    useEffect(() => {
        if (!pickId) {
            setItems([]);
            setPicked({});
            return;
        }
        loadItems(pickId);
    }, [pickId]);

    const selectedPick = picks.find((pick) => pick.id === pickId);
    const canReserve = selectedPick?.status === "open";
    const canComplete = selectedPick?.status === "in_progress";

    const selectPick = (value) => {
        setPickId(value);
        setFeedback(null);
    };

    const reserve = async () => {
        if (!canReserve) return;
        setReserveBusy(true);
        setFeedback(null);
        const response = await rpc.post(`/picklists/${pickId}/reserve`, {
            client_operation_id: flow.clientOperationId,
        });
        setReserveBusy(false);
        if (!response.ok) {
            setFeedback({ type: "error", text: response.error });
            return;
        }
        setFeedback({
            type: "success",
            text: "Pickliste reserviert. Gepickte Mengen können jetzt erfasst werden.",
        });
        await loadItems(pickId);
        await loadLists();
    };

    const missingPick = items.some((item) => !hasQuantity(picked[item.id], { min: 0 }));

    const complete = async () => {
        if (!canComplete) return;
        if (missingPick) {
            setFeedback({ type: "error", text: "Gepickte Menge für jede Position eingeben." });
            return;
        }
        setCompleteBusy(true);
        setFeedback(null);
        const response = await rpc.post(`/picklists/${pickId}/complete`, {
            client_operation_id: flow.clientOperationId,
            picked_quantities: picked,
        });
        setCompleteBusy(false);
        if (!response.ok) {
            setFeedback({ type: "error", text: response.error });
            return;
        }
        setFeedback({
            type: "success",
            text: "Pickliste abgeschlossen — gepickte Mengen sind gebucht.",
        });
        flow.rotate();
        setPickId("");
        await loadLists();
    };

    return (
        <FlowShell
            testId="flow-pickliste"
            title="Pickliste"
            kind="Step-Wizard"
            subtitle="Reservieren → Picken → Abschließen"
            onSubmit={canReserve ? reserve : canComplete ? complete : undefined}
            submitLabel={canReserve ? "Reservieren" : "Abschließen"}
            submittingLabel={canReserve ? "Reservierung läuft..." : "Abschluss läuft..."}
            submitDisabled={reserveBusy || completeBusy || (canComplete && missingPick)}
            submitting={reserveBusy || completeBusy}
            submitTestId={canReserve ? "pick-reserve" : canComplete ? "pick-complete" : undefined}
        >
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Aktive Pickliste
                </Label>
                <Select value={pickId} onValueChange={selectPick}>
                    <SelectTrigger className="h-12" data-testid="pick-select">
                        <SelectValue placeholder="Pickliste wählen" />
                    </SelectTrigger>
                    <SelectContent>
                        {picks.map((pick) => (
                            <SelectItem key={pick.id} value={pick.id}>
                                {pick.project_name || pick.id.slice(0, 8)} —{" "}
                                {STATUS_LABELS[pick.status] || pick.status}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {selectedPick ? (
                <div
                    className="rounded-md border border-border bg-card p-3 text-sm text-foreground"
                    data-testid="pick-status"
                >
                    Status: {STATUS_LABELS[selectedPick.status] || selectedPick.status}
                </div>
            ) : null}

            {items.length > 0 ? (
                <div className="flex flex-col gap-3" data-testid="pick-items">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Gepickte Mengen
                    </Label>
                    {items.map((item) => {
                        const position = positions[
                            item.picked_stock_position_id || item.suggested_stock_position_id
                        ];
                        const compartment = position && compartments[position.compartment_id];
                        return (
                        <div
                            key={item.id}
                            className="rounded-md border border-border bg-card p-3"
                            data-testid={`pick-item-${item.id}`}
                        >
                            <div className="mb-2 flex items-baseline justify-between text-sm">
                                <span className="font-medium">
                                    {articles[item.article_id]?.name || "Artikel"}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    angefordert: {item.requested_quantity}
                                </span>
                            </div>
                            <div className="mb-2 text-xs font-medium text-primary" data-testid={`pick-location-${item.id}`}>
                                Lagerort: {compartment?.code || compartment?.name || "nicht zugeordnet"}
                            </div>
                            {canComplete ? (
                                <QuantityStepper
                                    value={picked[item.id]}
                                    onChange={(value) =>
                                        setPicked((current) => ({
                                            ...current,
                                            [item.id]: value,
                                        }))
                                    }
                                    min={0}
                                    max={item.requested_quantity}
                                    ariaLabel={`Gepickte Menge für ${articles[item.article_id]?.name || "Artikel"}`}
                                    testId={`pick-qty-${item.id}`}
                                />
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    Mengen können nach der Reservierung angepasst werden.
                                </p>
                            )}
                        </div>
                    );})}
                </div>
            ) : null}

            {feedback ? (
                <div
                    role={feedback.type === "error" ? "alert" : "status"}
                    className="rounded-md border border-border bg-card p-3 text-sm text-foreground"
                    data-testid="pick-feedback"
                >
                    {feedback.type === "error" ? "Fehler: " : ""}
                    {feedback.text}
                </div>
            ) : null}

            {selectedPick ? (
                <div className="rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
                    {canReserve
                        ? "Reservieren sperrt die angeforderten Mengen für diese Pickliste."
                        : "Abschließen bucht die gepickten Mengen als Entnahme und gibt die Reservierung frei. Nicht gepickte Positionen auf 0 stellen."}
                </div>
            ) : null}
        </FlowShell>
    );
}
